import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getManagedProducers, searchAdminCompanies, transferPostingOwner, extractApiErrorMessage,
} from '../../../api/adminApi';

/** 검색어를 칠 때마다 요청하지 않도록 잠깐 기다린다 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * 현재 소유자인지 판별한다.
 *
 * 공고 응답의 companyId가 곧 producerProfile의 id다 (이름만 다르다).
 * 회사명 비교는 동명 제작사를 구분하지 못하고, 이관 후 목록이 갱신되지 않으면
 * 엉뚱한 항목을 잠그므로 ID가 있으면 항상 ID를 쓴다.
 */
export function isCurrentOwner(account, posting) {
  const ownerId = posting.companyId ?? posting.producerProfileId;
  if (ownerId != null && account.producerProfileId != null) {
    return Number(ownerId) === Number(account.producerProfileId);
  }
  return Boolean(posting.companyName) && account.companyName === posting.companyName;
}

/**
 * 대행 계정과 일반 제작사는 응답 모양이 달라 한 형태로 맞춘다.
 *
 * 대행 목록은 producer_profile.managed_by_admin(=어드민이 만들었다는 이력)으로 조회되는데,
 * 승격을 마친 계정도 그 이력이 남아 함께 잡힌다. 지금 상태는 accountType이 말해주므로
 * 그 값이 오면 그것을 따르고, 아직 안 오는 동안에는 종전대로 대행으로 본다.
 */
const fromManaged = (a) => ({
  producerProfileId: a.producerProfileId,
  companyName: a.companyName,
  email: a.email,
  isTest: a.isTest === true,
  managed: a.accountType ? a.accountType === 'MANAGED' : true,
});

const fromCompany = (c) => ({
  // 제작사 목록의 id가 곧 producerProfile의 id다
  producerProfileId: c.producerProfileId ?? c.id,
  companyName: c.companyName,
  email: c.email,
  isTest: c.isTest === true,
  managed: false,
});

/**
 * 대행 계정을 앞에 두고, 같은 제작사가 양쪽에 있으면 대행 쪽만 남긴다.
 * 대행 계정도 제작사 목록에 함께 잡히기 때문이다.
 */
export function mergeAccounts(managed, companies) {
  const seen = new Set();
  const out = [];
  [...managed.map(fromManaged), ...companies.map(fromCompany)].forEach(acc => {
    if (acc.producerProfileId == null) return;
    const key = String(acc.producerProfileId);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(acc);
  });
  return out;
}

/**
 * 공고 소유 제작사 이관 모달.
 *
 * 대행 계정과 일반 제작사를 함께 보여준다. 대행 계정은 수가 적고 자주 쓰이므로
 * 위에 모아두고, 일반 제작사는 수가 많아 검색으로 찾는다.
 *
 * 공고 ID·URL·지원자·조회수는 유지되고 소유자만 바뀐다.
 * 지원자 개인정보가 통째로 새 제작사에 넘어가므로 확정 전에 영향 범위를 먼저 보여준다.
 * 이관 횟수에는 제한이 없다. A → B → C 도, 다시 A 로 되돌리는 것도 같은 흐름이다.
 */
export default function TransferOwnerModal({ posting, onClose, onDone }) {
  const [managed, setManaged] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyTotal, setCompanyTotal] = useState(0);
  const [managedTotal, setManagedTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('managed');   // 'managed' | 'producer'
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // 늦게 도착한 예전 검색이 최신 결과를 덮어쓰지 않도록
  const reqIdRef = useRef(0);

  const loadCompanies = useCallback(async (q) => {
    const myReq = ++reqIdRef.current;
    setSearching(true);
    try {
      const { companies: list, total } = await searchAdminCompanies({ q, page: 1, limit: 30 });
      if (myReq !== reqIdRef.current) return;
      setCompanies(list);
      setCompanyTotal(total);
    } catch (err) {
      if (myReq !== reqIdRef.current) return;
      setError(extractApiErrorMessage(err, '제작사 목록을 불러오지 못했습니다.'));
      setCompanies([]);
      setCompanyTotal(0);
    } finally {
      if (myReq === reqIdRef.current) setSearching(false);
    }
  }, []);

  // 첫 진입 — 대행 계정과 제작사를 함께 불러온다
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [m] = await Promise.all([
          getManagedProducers({ page: 1, limit: 100 }).catch(err => ({ __failed: err })),
          loadCompanies(''),
        ]);
        if (cancelled) return;
        if (m?.__failed) {
          setError(extractApiErrorMessage(m.__failed, '대행 계정 목록을 불러오지 못했습니다.'));
          setManaged([]);
        } else {
          setManaged(m.accounts);
          setManagedTotal(m.total ?? m.accounts.length);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadCompanies]);

  // 검색어가 멎으면 제작사만 다시 조회한다 (대행 계정은 수가 적어 화면에서 거른다)
  useEffect(() => {
    const timer = setTimeout(() => loadCompanies(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, loadCompanies]);

  const term = query.trim().toLowerCase();
  const matches = (acc) => !term
    || (acc.companyName ?? '').toLowerCase().includes(term)
    || (acc.email ?? '').toLowerCase().includes(term);

  // mergeAccounts가 내부에서 변환하므로 원본 그대로 넘긴다 (제작사는 서버가 이미 걸렀다)
  const all = mergeAccounts(managed.filter(matches), companies);
  const managedList = all.filter(a => a.managed);
  const producerList = all.filter(a => !a.managed);

  // 대행 계정이 하나도 없으면 굳이 빈 구분을 보여주지 않는다
  const activeKind = kind === 'managed' && managedList.length === 0 && producerList.length > 0
    ? 'producer'
    : kind;
  const options = activeKind === 'managed' ? managedList : producerList;

  const applicants = posting.applicationCount ?? 0;

  /**
   * 404는 세 가지 원인이 같은 코드로 온다. 서버 메시지로 구분해 무엇을 고쳐야 할지 알린다.
   * 원문은 영문 기술 메시지라 그대로 띄우지 않는다.
   */
  const describeNotFound = (err) => {
    const raw = String(extractApiErrorMessage(err, '')).toLowerCase();
    if (raw.includes('casting post')) return '공고를 찾을 수 없습니다. 삭제되었을 수 있으니 목록을 새로고침해주세요.';
    if (raw.includes('producer profile')) return '선택한 제작사를 찾을 수 없습니다. 삭제되었을 수 있으니 다시 골라주세요.';
    return '공고 또는 제작사를 찾을 수 없습니다. 목록을 새로고침해주세요.';
  };

  const submit = async () => {
    if (!selected || saving) return;

    // 지원자 개인정보가 다른 회사로 넘어가는 일이라 되돌려도 이미 본 것은 되돌릴 수 없다.
    // 이력도 남지 않으므로 확정 전에 한 번 더 묻는다.
    if (applicants > 0) {
      const target = selected.companyName ?? `제작사 #${selected.producerProfileId}`;
      const ok = window.confirm(
        `'${posting.title}' 공고를 ${target} 소유로 옮깁니다.\n\n`
        + `지원자 ${applicants}명의 프로필과 연락처가 ${target}에 공개됩니다.\n`
        + '변경 이력은 남지 않습니다. 진행할까요?',
      );
      if (!ok) return;
    }

    setSaving(true);
    setError(null);
    try {
      await transferPostingOwner(posting.id, selected.producerProfileId);
      onDone(selected);
    } catch (err) {
      const code = err.response?.status;
      setError(
        code === 404
          ? describeNotFound(err)
          : extractApiErrorMessage(err, `이관에 실패했습니다.${code ? ` (HTTP ${code})` : ''}`),
      );
    } finally {
      setSaving(false);
    }
  };

  /** 드롭다운 한 줄에 담기는 설명 */
  const optionLabel = (acc) => {
    const name = acc.companyName ?? `제작사 #${acc.producerProfileId}`;
    if (isCurrentOwner(acc, posting)) return `${name} — 현재 소유`;
    if (acc.isTest) return `${name} — 테스트 계정, 이관 불가`;
    return acc.email ? `${name} — ${acc.email}` : name;
  };

  const selectable = (acc) => !isCurrentOwner(acc, posting) && !acc.isTest;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="공고 소유자 변경"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="ag-card"
        style={{ width: 540, maxWidth: '100%', maxHeight: '84vh', display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>공고 소유자 변경</div>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 6 }}>
            {posting.title}
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
            현재 소유 · {posting.companyName ?? '—'}
          </div>
        </div>

        <div style={{
          border: '1px solid var(--color-border)', borderRadius: 10, padding: '12px 14px',
          fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.7,
        }}>
          공고 주소와 조회수는 그대로 유지됩니다.
          {applicants > 0 && (
            <>
              <br />
              <b style={{ color: 'var(--color-danger)' }}>
                지원자 {applicants}명의 프로필과 연락처가 새 제작사에 전부 공개됩니다.
              </b>
            </>
          )}
          <br />
          기존 메시지 대화는 넘어가지 않습니다.
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ flex: '0 0 140px' }}>
            <label htmlFor="transfer-kind" style={{ display: 'block', fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>
              구분
            </label>
            <select
              id="transfer-kind"
              className="ag-field__input"
              value={activeKind}
              onChange={(e) => { setKind(e.target.value); setSelected(null); }}
              style={{ width: '100%' }}
            >
              <option value="managed">대행 계정 ({managedList.length})</option>
              <option value="producer">제작사 ({producerList.length})</option>
            </select>
          </div>

          {activeKind === 'producer' && (
            <div style={{ flex: '1 1 200px' }}>
              <label htmlFor="transfer-search" style={{ display: 'block', fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>
                제작사 검색
              </label>
              <input
                id="transfer-search"
                className="ag-field__input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="제작사명 또는 이메일"
                style={{ width: '100%' }}
              />
            </div>
          )}
        </div>

        <div>
          <label htmlFor="transfer-target" style={{ display: 'block', fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>
            새 소유 제작사
          </label>
          <select
            id="transfer-target"
            className="ag-field__input"
            value={selected?.producerProfileId ?? ''}
            disabled={loading || options.length === 0}
            onChange={(e) => {
              const found = options.find(a => String(a.producerProfileId) === e.target.value);
              setSelected(found ?? null);
            }}
            style={{ width: '100%' }}
          >
            <option value="">
              {loading
                ? '불러오는 중...'
                : options.length === 0
                  ? (term ? '검색 결과가 없습니다' : '고를 수 있는 제작사가 없습니다')
                  : '제작사를 선택하세요'}
            </option>
            {options.map(acc => (
              <option
                key={acc.producerProfileId}
                value={acc.producerProfileId}
                disabled={!selectable(acc)}
              >
                {optionLabel(acc)}
              </option>
            ))}
          </select>

          {activeKind === 'producer' && !term && companyTotal > companies.length && (
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 6 }}>
              제작사 {companyTotal}곳 중 {companies.length}곳만 목록에 있습니다. 검색으로 찾아주세요.
            </div>
          )}
          {activeKind === 'managed' && managedTotal > managed.length && (
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 6 }}>
              대행 계정 {managedTotal}개 중 {managed.length}개만 목록에 있습니다.
            </div>
          )}
          {searching && (
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 6 }}>검색 중...</div>
          )}
        </div>

        {error && (
          <div style={{ fontSize: 12, color: 'var(--color-danger)' }}>
            <i className="ti ti-alert-triangle" /> {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
          <button className="ag-btn ag-btn--sm" onClick={onClose} disabled={saving}>취소</button>
          <button
            className="ag-btn ag-btn--sm ag-btn--primary"
            onClick={submit}
            disabled={!selected || saving}
          >
            {saving ? '이관 중...' : selected ? `${selected.companyName ?? '선택한 제작사'}로 이관` : '제작사를 선택하세요'}
          </button>
        </div>
      </div>
    </div>
  );
}
